import React from 'react';
import { ProTable, ProColumns, ModalForm, ProFormText } from '@ant-design/pro-components';
import { Button, Space, Popconfirm, Tag, Avatar, Typography, Card, Statistic, Image } from 'antd';
import { PlusOutlined, DeleteOutlined, UserOutlined, ReloadOutlined, SyncOutlined } from '@ant-design/icons';
import { useAtom } from 'jotai';
import dayjs from 'dayjs';
import { useTrackedCreators, useCreateCreator, useDeleteCreator, useRefreshCreatorUserID } from '../api';
import { TrackedCreator, CreateCreatorRequest } from '../types';
import { addCreatorModalVisibleAtom } from '../atoms';

const { Text } = Typography;

const CreatorsTab: React.FC = () => {
    const [addModalVisible, setAddModalVisible] = useAtom(addCreatorModalVisibleAtom);

    const { data: creatorsData, isLoading } = useTrackedCreators();
    const createCreatorMutation = useCreateCreator();
    const deleteCreatorMutation = useDeleteCreator();
    const refreshUserIDMutation = useRefreshCreatorUserID();

    const creators = creatorsData?.data || [];

    const handleCreate = async (values: CreateCreatorRequest) => {
        try {
            await createCreatorMutation.mutateAsync(values);
            setAddModalVisible(false);
            return true;
        } catch (error) {
            return false;
        }
    };

    const handleDelete = async (id: string) => {
        await deleteCreatorMutation.mutateAsync(id);
    };

    const handleRefreshUserID = async (id: string) => {
        await refreshUserIDMutation.mutateAsync(id);
    };

    const columns: ProColumns<TrackedCreator>[] = [
        {
            title: 'Avatar',
            dataIndex: 'username',
            key: 'avatar',
            width: 80,
            render: (username: string, record: TrackedCreator) => {
                // Priority: S3 URL > base64 avatar > external service
                let avatarSrc = record.profile_pic_public_url;
                return (
                    <Avatar
                        size={40}
                        src={<Image preview src={avatarSrc} />}
                        icon={<UserOutlined />}
                    />
                );
            },
            search: false,
        },
        {
            title: 'Username',
            dataIndex: 'username',
            key: 'username',
            width: 150,
            render: (username: string) => (
                <Text strong>@{username}</Text>
            ),
        },
        {
            title: 'Display Name',
            dataIndex: 'displayName',
            key: 'displayName',
            width: 180,
            render: (name: string) => name || <Text type="secondary">No display name</Text>,
        },
        {
            title: 'Bio',
            dataIndex: 'bio',
            key: 'bio',
            ellipsis: true,
            width: 250,
            render: (bio: string) => (
                bio ? (
                    <Text ellipsis={{ tooltip: bio }} style={{ maxWidth: 230 }}>
                        {bio}
                    </Text>
                ) : (
                    <Text type="secondary">No bio</Text>
                )
            ),
        },
        {
            title: 'Followers',
            dataIndex: 'followerCount',
            key: 'followers',
            width: 120,
            render: (count: number) => <Text>{count.toLocaleString()}</Text>,
            sorter: true,
        },
        {
            title: 'Following',
            dataIndex: 'followingCount',
            key: 'following',
            width: 120,
            render: (count: number) => <Text>{count.toLocaleString()}</Text>,
            sorter: true,
        },
        {
            title: 'Posts',
            dataIndex: 'postCount',
            key: 'posts',
            width: 100,
            render: (count: number) => <Text>{count.toLocaleString()}</Text>,
            sorter: true,
        },
        {
            title: 'Total Content',
            dataIndex: 'total_content_count',
            key: 'totalContent',
            width: 120,
            render: (count: number) => <Text>{count ? count.toLocaleString() : 0}</Text>,
            sorter: true,
        },
        {
            title: 'Instagram User ID',
            dataIndex: 'instagramUserID',
            key: 'instagramUserID',
            width: 150,
            render: (userID: number, record: TrackedCreator) => (
                <Space direction="vertical" size={0}>
                    <Text code style={{ fontSize: '12px' }}>
                        {userID || <Text type="secondary">Not fetched</Text>}
                    </Text>
                    {!userID && (
                        <Button
                            type="link"
                            size="small"
                            icon={<SyncOutlined />}
                            onClick={() => handleRefreshUserID(record.id)}
                            loading={refreshUserIDMutation.isLoading}
                            style={{ padding: 0, height: 'auto' }}
                        >
                            Fetch ID
                        </Button>
                    )}
                </Space>
            ),
        },
        {
            title: 'Status',
            dataIndex: 'isActive',
            key: 'status',
            width: 100,
            render: (active: boolean) => (
                <Tag color={active ? 'green' : 'red'}>
                    {active ? 'ACTIVE' : 'INACTIVE'}
                </Tag>
            ),
        },
        {
            title: 'Added',
            dataIndex: 'createdAt',
            key: 'createdAt',
            width: 150,
            render: (date: string) => dayjs(date).format('MMM DD, YYYY'),
            sorter: true,
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 120,
            render: (_, record: TrackedCreator) => (
                <Space>
                    <Button
                        type="text"
                        onClick={() => window.open(`https://instagram.com/${record.username}`, '_blank')}
                        title="View Instagram profile"
                    >
                        View Profile
                    </Button>
                    {/*{record.instagramUserID && (*/}
                    {/*  <Button*/}
                    {/*    type="text"*/}
                    {/*    icon={<ReloadOutlined />}*/}
                    {/*    onClick={() => handleRefreshUserID(record.id)}*/}
                    {/*    loading={refreshUserIDMutation.isLoading}*/}
                    {/*    title="Refresh Instagram User ID"*/}
                    {/*  >*/}
                    {/*    Refresh ID*/}
                    {/*  </Button>*/}
                    {/*)}*/}
                    <Popconfirm
                        title="Delete Creator"
                        onConfirm={() => handleDelete(record.id)}
                        okText="Yes"
                        cancelText="No"
                        okButtonProps={{ danger: true }}
                    >
                        <Button
                            type="text"
                            danger
                            icon={<DeleteOutlined />}
                            loading={deleteCreatorMutation.isLoading}
                            title="Delete creator"
                        />
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <div>
            <Card style={{ marginBottom: 16 }}>
                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                    <Text>
                        Manage Instagram creators that are actively tracked for content scraping.
                    </Text>
                    <Text type="secondary">
                        Total creators: {creators.length} |
                        Active: {creators.filter(c => c.isActive).length} |
                        Inactive: {creators.filter(c => !c.isActive).length}
                    </Text>
                </Space>
            </Card>

            <ProTable<TrackedCreator>
                columns={columns}
                dataSource={creators}
                rowKey="id"
                loading={isLoading}
                search={{
                    labelWidth: 'auto',
                }}
                toolbar={{
                    actions: [
                        <Button
                            key="add"
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={() => setAddModalVisible(true)}
                        >
                            Add Creator
                        </Button>,
                    ],
                }}
                pagination={{
                    defaultPageSize: 10,
                    showSizeChanger: true,
                    showQuickJumper: true,
                    pageSizeOptions: ['10', '20', '50'],
                }}
                scroll={{ x: 1400 }}
            />

            <ModalForm<CreateCreatorRequest>
                title="Add Instagram Creator"
                open={addModalVisible}
                onOpenChange={setAddModalVisible}
                onFinish={handleCreate}
                modalProps={{
                    destroyOnClose: true,
                }}
                submitter={{
                    submitButtonProps: {
                        loading: createCreatorMutation.isLoading,
                    },
                }}
                layout="horizontal"
                labelCol={{ span: 6 }}
                wrapperCol={{ span: 18 }}
            >
                <ProFormText
                    name="username"
                    label="Username"
                    placeholder="Enter Instagram username (without @)"
                    rules={[
                        { required: true, message: 'Please enter username' },
                        {
                            pattern: /^[a-zA-Z0-9._]+$/,
                            message: 'Invalid username format'
                        },
                    ]}
                    fieldProps={{
                        addonBefore: '@',
                    }}
                    extra="Enter the Instagram username without the @ symbol"
                />
            </ModalForm>
        </div>
    );
};

export default CreatorsTab;